import { list, getProperties } from "aws-amplify/storage";

export interface VersionInfo {
  versionId: string;
  eTag: string;
  lastModified: Date;
  size: number;
  contentHash?: string;
}

/**
 * Versioning Service - Thin layer on top of your existing code
 * Only adds versioning-specific functionality
 */
export class FileVersioningService {
  /**
   * Calculate content hash for deduplication using Web Crypto API
   * This is the ONLY new functionality - everything else uses your existing code
   */
  async calculateContentHash(file: File | ArrayBuffer): Promise<string> {
    let buffer: ArrayBuffer;

    if (file instanceof File) {
      buffer = await file.arrayBuffer();
    } else {
      buffer = file;
    }

    const hashBuffer = await crypto.subtle.digest("SHA-256", buffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
  }

  /**
   * Get S3 file metadata using Amplify Storage (not S3 SDK)
   * Uses your existing Amplify setup
   */
  async getFileMetadata(path: string): Promise<{
    eTag: string;
    contentType?: string;
    size: number;
    lastModified: Date;
    metadata?: Record<string, string>;
  }> {
    const properties = await getProperties({ path });

    return {
      eTag: properties.eTag || "",
      contentType: properties.contentType,
      size: properties.size || 0,
      lastModified: properties.lastModified || new Date(),
      metadata: properties.metadata,
    };
  }

  /**
   * List all versions of a file using Amplify Storage
   * Works with S3 versioning at bucket level
   */
  async listFileVersions(basePath: string): Promise<VersionInfo[]> {
    try {
      // List all items in the directory
      const result = await list({
        path: basePath,
        options: {
          listAll: true,
        },
      });

      const versions: VersionInfo[] = [];

      for (const item of result.items) {
        if (item.path && item.eTag) {
          versions.push({
            versionId: item.eTag, // Use eTag as version identifier
            eTag: item.eTag,
            lastModified: item.lastModified || new Date(),
            size: item.size || 0,
          });
        }
      }

      // Sort by last modified (newest first)
      return versions.sort(
        (a, b) => b.lastModified.getTime() - a.lastModified.getTime()
      );
    } catch (error) {
      console.error("Error listing file versions:", error);
      return [];
    }
  }

  /**
   * Check if file with same content hash exists
   * Uses metadata from your existing uploads
   */
  async findDuplicateByHash(
    userPath: string,
    contentHash: string
  ): Promise<string | null> {
    try {
      const result = await list({
        path: userPath,
        options: {
          listAll: true,
        },
      });

      for (const item of result.items) {
        if (item.path) {
          try {
            const properties = await getProperties({ path: item.path });

            // Check if contenthash metadata matches
            if (properties.metadata?.["contenthash"] === contentHash) {
              return item.path;
            }
          } catch (error) {
            // Skip files we can't access
            continue;
          }
        }
      }

      return null;
    } catch (error) {
      console.error("Error finding duplicate:", error);
      return null;
    }
  }

  /**
   * Determine what to do with uploaded file
   * Returns action for Lambda to execute
   */
  async determineVersioningAction(
    uploadedPath: string,
    fileName: string,
    contentHash: string
  ): Promise<{
    action: "KEEP" | "DEDUPLICATE" | "VERSION";
    existingPath?: string;
    reason: string;
  }> {
    const pathParts = uploadedPath.split("/");
    const userBasePath = pathParts.slice(0, -1).join("/");

    // Get file extension
    const extension = fileName.split(".").pop()?.toLowerCase() || "";

    // Check for existing file with same content
    const duplicatePath = await this.findDuplicateByHash(
      userBasePath,
      contentHash
    );

    if (duplicatePath) {
      // Check if same extension
      const duplicateExt = duplicatePath.split(".").pop()?.toLowerCase() || "";

      if (duplicateExt === extension) {
        // Scenario 2 & 4: Same content, same extension
        return {
          action: "DEDUPLICATE",
          existingPath: duplicatePath,
          reason: "Identical file already exists",
        };
      } else {
        // Scenario 3: Same content, different extension
        return {
          action: "KEEP",
          reason: "Different file type (extension)",
        };
      }
    }

    // Scenario 1 & 5: New content or new file
    // S3 versioning handles this automatically
    return {
      action: "VERSION",
      reason: "New content or new file",
    };
  }

  /**
   * Generate secure filename from hash
   * Used by Lambda for renaming
   */
  generateSecureFilename(contentHash: string, extension: string): string {
    // Use first 32 chars of hash for filename
    const hashPart = contentHash.substring(0, 32);
    return `${hashPart}${extension}`;
  }
}

export const fileVersioningService = new FileVersioningService();
